import React, { useState, useMemo, useEffect } from "react";
import { Dropdown, Spinner, Button, Alert, Form } from "react-bootstrap";

import Pagination from "../../components/Pagination/Pagination";
import UserForm from "./_components/UserForm";
import DeleteModal from "../../components/DeleteModal";

import { useDeleteInspectionUserMutation, useInspectionUsersQuery } from "../../../api/inspection/inspectionUsers.hooks";
import { useDeleteResortUserMutation, useResortsUsersQuery } from "../../../api/resort-booking/resort.hooks";
import { useCloudUsersQuery } from "../../../api/user/user.hooks";
import {
  useAllUsersQuery,
  useDeleteUserMutation,
} from "../../../api/main-site-service/main-site-service.hooks";
import {
  useDeleteUserMutation as useDeleteFleetUserMutation,
  useGetUsers as useFleetUsersQuery,
} from "../../../api/fleet-management/users/users.hooks";
import { useGetUsersByRole as useJeepRallyUsersByRoleQuery } from "../../../api/jeep-rally/users/users.hooks";

const normalizeJeepRallyUsers = (data) => {
  if (Array.isArray(data)) return data.filter(Boolean);
  if (Array.isArray(data?.data)) return data.data.filter(Boolean);
  if (Array.isArray(data?.users)) return data.users.filter(Boolean);
  return [];
};

const User = () => {
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    type: "",
    service: "",
  });
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  });
  const [showFormView, setShowFormView] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const {
    users,
    loading,
    error,
    refetch: refetchCloudUsers,
  } = useCloudUsersQuery();
  const {
    inspectionUsers,
    loading: loadingInspection,
    error: errorInspection,
    refetch: refetchInspectionUsers,
  } = useInspectionUsersQuery();
  const {
    resortsUsers,
    loading: loadingResorts,
    error: errorResorts,
    refetch: refetchResortsUsers,
  } = useResortsUsersQuery("users");
  const {
    users: mainSiteUsers,
    isLoading: loadingMainSite,
    error: errorMainSite,
    refetch: refetchMainSiteUsers,
  } = useAllUsersQuery();
  const {
    data: fleetUsersResponse,
    isLoading: loadingFleetUsers,
    error: errorFleetUsers,
    refetch: refetchFleetUsers,
  } = useFleetUsersQuery();
  const {
    data: jeepRallyUsersResponse,
    isLoading: loadingJeepRallyUsers,
    error: errorJeepRallyUsers,
    refetch: refetchJeepRallyUsers,
  } = useJeepRallyUsersByRoleQuery("admin");

  const fleetUsers = useMemo(() => {
    if (Array.isArray(fleetUsersResponse)) return fleetUsersResponse;
    return fleetUsersResponse;
  }, [fleetUsersResponse]);

  const jeepRallyUsers = useMemo(
    () => normalizeJeepRallyUsers(jeepRallyUsersResponse),
    [jeepRallyUsersResponse]
  );

  const { mutate: deleteUser, isLoading: isDeleting } = useDeleteUserMutation();
  const { deleteResortUser, loading: isDeletingResortUser, error: errorDeletingResortUser } = useDeleteResortUserMutation()
  
  const { deleteInspectionUser, loading: isDeletingInspectionUser, error: deleteError } = useDeleteInspectionUserMutation();
  const { mutateAsync: deleteFleetUser, isPending: isDeletingFleetUser } = useDeleteFleetUserMutation();

  const showAlert = (message, variant = "info") => {
    setAlert({ show: true, message, variant });
    setTimeout(
      () => setAlert({ show: false, message: "", variant: "info" }),
      5000
    );
  };
  // Combine all users from different sources
  const tableData = useMemo(() => {
    const allUsers = [];

    // Add main users (only if no error)
    if (Array.isArray(users) && !error) {
      users.forEach((user, index) => {
        allUsers.push({
          id: user._id || user.id || `main-${index}`,
          name: user.name || "-",
          email: user.email || "-",
          role: user.roles || "-",
          module: user.module === "*" ? "All" : user.module || "-",
          service: user.Service === "*" ? "All" : user.Service || "-",
          type: "e-ticketing",
          createdAt: user.createdAt || "-",
          fullUser: { ...user, userType: "main" },
        });
      });
    }

    // Add inspection users (only if no error)
    if (Array.isArray(inspectionUsers) && !errorInspection) {
      inspectionUsers.forEach((user, index) => {
        allUsers.push({
          id: user._id || user.id || `inspection-${index}`,
          name: user.name || "-",
          email: user.email || "-",
          role: user.role || "-",
          module: "Inspection App",
          service: "*",
          type: "Inspection App",
          createdAt: user.createdAt || "-",
          fullUser: { ...user, userType: "inspection" },
        });
      });
    }

    // Add resort users (only if no error)
    if (Array.isArray(resortsUsers) && !errorResorts) {
      resortsUsers.forEach((user, index) => {
        const userRole = (user.role || "").toLowerCase();
        const allowedRoles = ["admin", "hoteladmin", "reservationadmin"];
        if (!allowedRoles.includes(userRole)) {
          return; // Skip users with other roles
        }

        allUsers.push({
          id: user.id || user._id || `resort-${index}`,
          name: user.name || "-",
          email: user.email || "-",
          role: user.role || "-",
          module: "Resort Booking",
          service:  "-",
          type: "Resort Booking",
          createdAt: user.createdAt
            ? user.createdAt.seconds
              ? new Date(user.createdAt.seconds * 1000).toISOString()
              : user.createdAt
            : "-",
          fullUser: { ...user, userType: "resort" },
        });
      });
    }

    // Add main site users (only if no error)
    if (Array.isArray(mainSiteUsers) && !errorMainSite) {
      mainSiteUsers.forEach((user, index) => {
        allUsers.push({
          id: user._id || user.id || `mainsite-${index}`,
          name: user.name || "-",
          email: user.email || "-",
          role: user.role || "-",
          module: "Main Site",
          service: "-",
          type: "Main Site",
          isActive: user.is_active,
          createdAt: user.createdAt || "-",
          fullUser: { ...user, userType: "mainsite" },
        });
      });
    }

    // Add fleet users (only if no error)
    if (Array.isArray(fleetUsers) && !errorFleetUsers) {
      fleetUsers.forEach((user, index) => {
        allUsers.push({
          id: user._id || user.id || `fleet-${index}`,
          name: user.name || "-",
          email: user.email || "-",
          role: user.role || "-",
          module: "Fleet Management",
          service: "-",
          type: "Fleet Management",
          isActive: user.isActive,
          createdAt: user.createdAt || "-",
          fullUser: { ...user, userType: "fleet" },
        });
      });
    }

    // Add jeep rally users (only if no error)
    if (Array.isArray(jeepRallyUsers) && !errorJeepRallyUsers) {
      jeepRallyUsers.forEach((user, index) => {
        allUsers.push({
          id: user._id || user.id || `jeep-rally-${index}`,
          name: user.name || "-",
          email: user.email || "-",
          role: user.role || "-",
          module: "Jeep Rally",
          service: "-",
          type: "Jeep Rally",
          createdAt: user.createdAt || user.created_at || "-",
          fullUser: { ...user, userType: "jeep-rally" },
        });
      });
    }

    return allUsers;
  }, [users, inspectionUsers, resortsUsers, mainSiteUsers, fleetUsers, jeepRallyUsers, error, errorInspection, errorResorts, errorMainSite, errorFleetUsers, errorJeepRallyUsers]);

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    const types = new Set();
    const services = new Set();

    tableData.forEach((user) => {
      if (user.type) types.add(user.type);
      // Only collect services from e-ticketing users
      if (user.type === "e-ticketing" && user.service && user.service !== "-") {
        services.add(user.service);
      }
    });

    return {
      types: Array.from(types).sort(),
      services: Array.from(services).sort(),
    };
  }, [tableData]);

  const filtered = useMemo(() => {
    let result = tableData;

    // Apply search filter
    if (searchTerm) {
      result = result.filter((user) =>
        [user.name, user.email, user.role, user.module, user.service, user.type]
          .join(" ")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filters.type) {
      result = result.filter((user) => user.type === filters.type);
    }

    // Apply service filter
    if (filters.service) {
      result = result.filter((user) => user.service === filters.service);
    }

    // Sort alphabetically by name
    result.sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return result;
  }, [tableData, searchTerm, filters]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const paginated = useMemo(() => {
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  }, [filtered, currentPage, itemsPerPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleAddUser = () => {
    setFormMode("create");
    setSelectedUser(null);
    setShowFormView(true);
  };

  const handleEditUser = (user) => {
    setFormMode("edit");
    setSelectedUser(user);
    setShowFormView(true);
  };

  const handleViewUser = (user) => {
    setFormMode("view");
    setSelectedUser(user);
    setShowFormView(true);
  };

  const handleSaveUser = async (userData) => {
    try {
      // Close form first to prevent flickering
      setShowFormView(false);
      setSelectedUser(null);
      
      // Show immediate feedback
      if (formMode === "create") {
        showAlert("Creating user...", "info");
      } else {
        showAlert("Updating user...", "info");
      }

      // Small delay to ensure backend has processed the request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refetch all user sources in parallel
      await Promise.all([
        refetchMainSiteUsers(),
        refetchCloudUsers(),
        refetchInspectionUsers(),
        refetchResortsUsers(),
        refetchFleetUsers(),
        refetchJeepRallyUsers(),
      ]);
      
      // Show success message
      if (formMode === "create") {
        showAlert("User created successfully!", "success");
      } else {
        showAlert("User updated successfully!", "success");
      }
    } catch (error) {
      console.error("Error saving user:", error);
      showAlert(
        error?.response?.data?.message || "Failed to save user",
        "danger"
      );
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      if (userToDelete.userType === "mainsite") {
        await deleteUser(userToDelete._id || userToDelete.id);
        
        setShowDeleteModal(false);
        setUserToDelete(null);
        
        showAlert("Deleting user...", "info");
        
        // Small delay to ensure backend has processed the request
        await new Promise(resolve => setTimeout(resolve, 500));
        
        await refetchMainSiteUsers();
        showAlert("User deleted successfully!", "success");
      } else if (userToDelete.userType === "resort") {
        await deleteResortUser(userToDelete.id);
        
        setShowDeleteModal(false);
        setUserToDelete(null);
        
        showAlert("Deleting resort user...", "info");
        
        // Small delay to ensure backend has processed the request
        await new Promise(resolve => setTimeout(resolve, 500));
        
        await refetchResortsUsers();
        showAlert("Resort user deleted successfully!", "success");
      } else if (userToDelete.userType === "inspection") {
        await deleteInspectionUser(userToDelete._id || userToDelete.id);
        
        setShowDeleteModal(false);
        setUserToDelete(null);
        
        showAlert("Deleting inspection user...", "info");
        
        // Small delay to ensure backend has processed the request
        await new Promise(resolve => setTimeout(resolve, 500));
        
        await refetchInspectionUsers();
        showAlert("Inspection user deleted successfully!", "success");
      } else if (userToDelete.userType === "fleet") {
        await deleteFleetUser(userToDelete._id || userToDelete.id);

        setShowDeleteModal(false);
        setUserToDelete(null);

        showAlert("Deleting fleet user...", "info");

        await new Promise(resolve => setTimeout(resolve, 500));

        await refetchFleetUsers();
        showAlert("Fleet user deleted successfully!", "success");
      } else {
        showAlert(
          "Deleting other user types is not yet implemented",
          "warning"
        );
        setShowDeleteModal(false);
        setUserToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      showAlert(
        error?.response?.data?.message || "Failed to delete user",
        "danger"
      );
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const handleCancelForm = () => {
    setShowFormView(false);
    setSelectedUser(null);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setCurrentPage(0); // Reset to first page when filter changes
  };

  const handleClearFilters = () => {
    setFilters({
      type: "",
      service: "",
    });
    setSearchTerm("");
    setCurrentPage(0);
  };

  const isLoadingAny = loading || loadingInspection || loadingResorts || loadingMainSite || loadingFleetUsers || loadingJeepRallyUsers;
  
  // Check if ANY data source has an error (not all)
  const hasAnyError = error || errorInspection || errorResorts || errorMainSite || errorFleetUsers || errorJeepRallyUsers;
  
  // Only show critical error if ALL requests failed and we have no data
  const hasCriticalError = 
    error && errorInspection && errorResorts && errorMainSite && errorFleetUsers && errorJeepRallyUsers && 
    tableData.length === 0 && 
    !isLoadingAny;
  
  const hasActiveFilters = searchTerm || Object.values(filters).some((f) => f);

  // Only show critical error if ALL APIs failed
  if (hasCriticalError) {
    return (
      <div className="col-12">
        <Alert variant="danger" className="d-flex justify-content-between align-items-center">
          <span>Error loading all user data. Please try again later.</span>
          <Button 
            variant="outline-danger" 
            size="sm"
            onClick={() => {
              refetchCloudUsers();
              refetchInspectionUsers();
              refetchResortsUsers();
              refetchMainSiteUsers();
              refetchFleetUsers();
              refetchJeepRallyUsers();
            }}
          >
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="col-12">
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() =>
            setAlert({ show: false, message: "", variant: "info" })
          }
          className="mb-3"
        >
          {alert.message}
        </Alert>
      )}

      {/* Show warning if some APIs failed but we still have data */}
      {hasAnyError && !isLoadingAny && tableData.length > 0 && (
        <Alert variant="warning" className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Warning:</strong> Some user sources failed to load. 
              {error && " E-Ticketing users unavailable."}
              {errorInspection && " Inspection users unavailable."}
              {errorResorts && " Resort users unavailable."}
              {errorMainSite && " Main Site users unavailable."}
              {errorFleetUsers && " Fleet users unavailable."}
              {errorJeepRallyUsers && " Jeep Rally users unavailable."}
            </div>
            <Button 
              variant="outline-warning" 
              size="sm"
              onClick={() => {
                if (error) refetchCloudUsers();
                if (errorInspection) refetchInspectionUsers();
                if (errorResorts) refetchResortsUsers();
                if (errorMainSite) refetchMainSiteUsers();
                if (errorFleetUsers) refetchFleetUsers();
                if (errorJeepRallyUsers) refetchJeepRallyUsers();
              }}
            >
              Retry Failed
            </Button>
          </div>
        </Alert>
      )}

      {!showFormView ? (
        // --- TABLE VIEW ---
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="card-title m-0">
              Users {!isLoadingAny && `(${filtered.length})`}
            </h4>
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <input
                type="text"
                className="form-control"
                style={{ width: "200px" }}
                placeholder="Search users"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {hasActiveFilters && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </Button>
              )}

              <Form.Select
                value={filters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
                style={{ width: "150px" }}
              >
                <option value="">All Modules</option>
                {filterOptions.types.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Form.Select>

              {filters.type === "e-ticketing" && (
                <Form.Select
                  value={filters.service}
                  onChange={(e) => handleFilterChange("service", e.target.value)}
                  style={{ width: "150px" }}
                >
                  <option value="">All Services</option>
                  {filterOptions.services.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </Form.Select>
              )}

              <Button onClick={handleAddUser}>+ Add User</Button>
            </div>
          </div>

          <div className="card-body">
            {isLoadingAny ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" />
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Module</th>
                        <th>Service</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((user, i) => (
                        <tr key={user.id}>
                          <td>{currentPage * itemsPerPage + i + 1}</td>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>{user.role}</td>
                          <td>{user.module}</td>
                          <td>{user.service}</td>
                          <td>
                            <Dropdown>
                              <Dropdown.Toggle variant="link">
                                &#x2022;&#x2022;&#x2022;
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end">
                                <Dropdown.Item
                                  onClick={() => handleViewUser(user.fullUser)}
                                >
                                  View
                                </Dropdown.Item>
                                {user.type === "Main Site" && (
                                  <>
                                    <Dropdown.Item
                                      onClick={() => handleEditUser(user.fullUser)}
                                    >
                                      Edit
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() => handleDeleteClick(user.fullUser)}
                                      className="text-danger"
                                    >
                                      Delete
                                    </Dropdown.Item>
                                  </>
                                )}
                                {user.type === "Resort Booking" && (
                                  <>
                                    <Dropdown.Item
                                      onClick={() => handleEditUser(user.fullUser)}
                                    >
                                      Edit
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() => handleDeleteClick(user.fullUser)}
                                      className="text-danger"
                                    >
                                      Delete
                                    </Dropdown.Item>
                                  </>
                                )}
                                {user.type === "Inspection App" && (
                                  <>
                                    <Dropdown.Item
                                      onClick={() => handleEditUser(user.fullUser)}
                                    >
                                      Edit
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() => handleDeleteClick(user.fullUser)}
                                      className="text-danger"
                                    >
                                      Delete
                                    </Dropdown.Item>
                                  </>
                                )}
                                {user.type === "Fleet Management" && (
                                  <>
                                    <Dropdown.Item
                                      onClick={() => handleEditUser(user.fullUser)}
                                    >
                                      Edit
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() => handleDeleteClick(user.fullUser)}
                                      className="text-danger"
                                    >
                                      Delete
                                    </Dropdown.Item>
                                  </>
                                )}
                                {user.type === "Jeep Rally" && (
                                  <Dropdown.Item
                                    onClick={() =>
                                      showAlert(
                                        "Delete functionality coming soon!",
                                        "info"
                                      )
                                    }
                                    className="text-danger"
                                  >
                                    Delete
                                  </Dropdown.Item>
                                )}
                                {user.type !== "Main Site" && user.type !== "Resort Booking" && user.type !== "Inspection App" && user.type !== "Fleet Management" && user.type !== "Jeep Rally" && (
                                  <>
                                    <Dropdown.Item
                                      onClick={() => handleEditUser(user.fullUser)}
                                    >
                                      Edit
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() =>
                                        showAlert(
                                          "Delete functionality coming soon!",
                                          "info"
                                        )
                                      }
                                      className="text-danger"
                                    >
                                      Delete
                                    </Dropdown.Item>
                                  </>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={filtered.length}
                  onPageChange={handlePageChange}
                />

                {filtered.length === 0 && !isLoadingAny && (
                  <div className="text-center py-4">
                    <p className="text-muted">No users found</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        // --- FORM VIEW ---
        <UserForm
          onCancel={handleCancelForm}
          onSave={handleSaveUser}
          user={selectedUser}
          mode={formMode}
          isLoading={false}
        />
      )}

      <DeleteModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setUserToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete User"
        message={`Are you sure you want to delete user "${userToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isDeleting || isDeletingResortUser || isDeletingInspectionUser || isDeletingFleetUser}
        danger={true}
      />
    </div>
  );
};

export default User;
